import { Label } from '@nimbus-ds/components';
import { AiFeature } from '@tiendanube/common/src/enums';
import { FEATURE_AI_TIER } from 'App/features';
import {
  DEFAULT_LANGUAGE,
  LanguageInterface,
  LanguagesType,
  LanguageNameValueInterface,
} from 'App/i18n';
import { useResponsive, Stack, Editor, Input } from 'commons/components';
import GenerateWithAiLink from 'commons/components/GenerateWithAiLink';
import { useAiGeneratedValues, useAutoFocus, useModal } from 'commons/hooks';
import { AI_FIELDS } from 'commons/services/AgentChatService/AgentChatActionBusTypes';
import { useAiUsage } from 'domains/Ai/hooks';
import {
  trackingAiProductDescriptionEdit,
  trackingAiProductNameEdit,
} from 'domains/Ai/tracking';
import { useGetLanguage } from 'domains/Auth/hooks';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import type { FormNameAndDescriptionNavigationAttributes } from './types';
import AIProductDescriptionGeneratorModal from '../AIProductDescriptionGeneratorModal';
import type {
  IsGeneratedAIType,
  SetIsFieldGeneratedAIType,
} from '../hooks/useNameAndDescriptionAI';

function decodeHTMLEntities(str: string) {
  const txt = document.createElement('textarea');
  txt.innerHTML = str;
  return txt.value;
}

export interface FormNameAndDescriptionProps {
  name: LanguageInterface;
  description?: LanguageInterface;
  onChange: (data: LanguageNameValueInterface) => void;
  language?: LanguagesType;
  errors: Partial<Record<string, string>>;
  autoFocus?: boolean;
  isGeneratedAI: IsGeneratedAIType;
  setIsFieldGeneratedAI: SetIsFieldGeneratedAIType;
  aiNavigationAttributes?: FormNameAndDescriptionNavigationAttributes;
}

function FormNameAndDescription({
  name,
  errors,
  description,
  onChange,
  language = DEFAULT_LANGUAGE,
  autoFocus,
  isGeneratedAI,
  setIsFieldGeneratedAI,
  aiNavigationAttributes,
}: FormNameAndDescriptionProps): JSX.Element {
  const t = useTranslationCatalog();
  const languageStore = useGetLanguage();
  const { isDesktop } = useResponsive();
  const { ref } = useAutoFocus();
  const [
    showProductDescriptionGeneratorModal,
    openProductDescriptionGeneratorModal,
    closeProductDescriptionGeneratorModal,
  ] = useModal();

  const hasError = !!errors[`name.${language}`];

  const { isLoadingAiUses, requiredTier, aiUsesUpsellTitle } = useAiUsage();

  const handleOpenProductDescriptionGeneratorModal = useUpsellFlow({
    title: aiUsesUpsellTitle,
    featureKey: FEATURE_AI_TIER,
    trackingSource: AiFeature.AI_PRODUCTS_DESCRIPTION,
    callback: openProductDescriptionGeneratorModal,
    minValue: requiredTier,
  });

  const {
    isAiGenerated: productNameAiGenerated,
    resetAiGeneratedValues: resetProductNameAiGenerated,
  } = useAiGeneratedValues<LanguageNameValueInterface>(
    AI_FIELDS.PRODUCT_NAME,
    language,
  );

  const {
    isAiGenerated: productDescriptionAiGenerated,
    resetAiGeneratedValues: resetProductDescriptionAiGenerated,
  } = useAiGeneratedValues<LanguageNameValueInterface>(
    AI_FIELDS.PRODUCT_DESCRIPTION,
    language,
  );

  const handleChangeName = (params: { name: string; value: string }) => {
    onChange({
      name: 'name',
      value: { ...name, [language]: params.value },
    });

    resetProductNameAiGenerated();

    if (isGeneratedAI.name[language]) {
      trackingAiProductNameEdit(
        {
          feature: AiFeature.AI_PRODUCTS_NAME,
        },
        language,
      );
      setIsFieldGeneratedAI('name', language, false);
    }
  };

  const handleChangeEditor = (value: string, isUserInput = false): void => {
    onChange({
      name: 'description',
      value: { ...description, [language]: value },
    });

    if (isUserInput) {
      resetProductDescriptionAiGenerated();
    }

    const hasContentChanged =
      decodeHTMLEntities(value) !== description?.[language];

    if (hasContentChanged && isGeneratedAI.description[language]) {
      trackingAiProductDescriptionEdit(
        {
          feature: AiFeature.AI_PRODUCTS_DESCRIPTION,
        },
        language,
      );
      setIsFieldGeneratedAI('description', language, false);
    }
  };

  return (
    <>
      <AIProductDescriptionGeneratorModal
        show={showProductDescriptionGeneratorModal}
        product={{
          name: name[language],
          lang: language,
        }}
        onUseDescription={(newDescription: string) => {
          onChange({
            name: 'description',
            value: { ...description, [language]: newDescription },
          });
          setIsFieldGeneratedAI('description', language, true);
        }}
        onDismiss={closeProductDescriptionGeneratorModal}
      />
      <Stack innerRef={autoFocus && ref} column align="stretch" spacing="loose">
        <Input
          type="text"
          label={t('products.detail.name')}
          placeholder={t('products.detail.sampleName')}
          onChange={handleChangeName}
          name="name"
          maxLength={150}
          value={name[language]}
          appearance={hasError ? 'danger' : undefined}
          helpText={hasError ? t('products.errorName') : undefined}
          aiGenerated={isGeneratedAI.name[language] || productNameAiGenerated}
          {...aiNavigationAttributes?.name}
        />
        <Stack column spacing="tight" align="stretch">
          <Stack spacing="tight">
            <Stack.Item>
              <Label>{t('products.detail.description')}</Label>
            </Stack.Item>
            {!isLoadingAiUses && (
              <Stack.Item>
                <GenerateWithAiLink
                  trackingFeature={AiFeature.AI_PRODUCTS_DESCRIPTION}
                  onClick={handleOpenProductDescriptionGeneratorModal}
                  fontSize="caption"
                />
              </Stack.Item>
            )}
          </Stack>
          <Editor
            value={description ? description[language] || '' : ''}
            onChange={handleChangeEditor}
            language={languageStore}
            showToolbar={isDesktop}
            isGeneratedAI={
              isGeneratedAI.description[language] ||
              productDescriptionAiGenerated
            }
            navigationAttributes={aiNavigationAttributes?.description}
          />
        </Stack>
      </Stack>
    </>
  );
}

export default FormNameAndDescription;
